import { Alert, Button, DatePicker, Form, InputNumber, Modal } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useNextFreeWindow } from '../hooks/use-next-free-window';
import { formatWindow } from '../policies/booking-labels';

interface NextFreeWindowModalProps {
  open: boolean;
  truckId: string;
  /** `YYYY-MM-DD`: when the truck's soonest time-based service falls due, to start from; null when it has none to count from. */
  defaultDueBy: string | null;
  /** Null hides "Book this window" for someone who can look but not book. */
  onBook: ((window: { startsAt: string; endsAt: string }) => void) | null;
  onClose: () => void;
}

interface FormFields {
  durationHours: number;
  dueBy: Dayjs;
}

/** Finds the next gap between the truck's trips and bookings that fits the service and ends before it falls due. */
export function NextFreeWindowModal({ open, truckId, defaultDueBy, onBook, onClose }: NextFreeWindowModalProps) {
  const [form] = Form.useForm<FormFields>();
  const { state, search, reset } = useNextFreeWindow(truckId);

  async function find({ durationHours, dueBy }: FormFields) {
    await search({ durationMinutes: Math.round(durationHours * 60), dueBy: dueBy.endOf('day').toISOString() });
  }

  return (
    <Modal
      open={open}
      onCancel={() => {
        reset();
        onClose();
      }}
      footer={null}
      title="Find the next free window"
      destroyOnHidden
    >
      <Form<FormFields>
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={find}
        onValuesChange={reset}
        disabled={state.status === 'searching'}
        initialValues={{ durationHours: 4, dueBy: defaultDueBy ? dayjs(defaultDueBy) : undefined }}
      >
        <Form.Item name="durationHours" label="How long the service takes (hours)" rules={[{ required: true, message: 'Give the length of the service' }]}>
          <InputNumber className="w-full" min={0.25} max={336} step={0.5} />
        </Form.Item>
        <Form.Item
          name="dueBy"
          label="Must be done by"
          extra={defaultDueBy ? 'Taken from when its next time-based service falls due.' : undefined}
          rules={[{ required: true, message: 'Choose the date it must be done by' }]}
        >
          <DatePicker className="w-full" />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={state.status === 'searching'}>
          Find a window
        </Button>
      </Form>
      <NextFreeWindowAnswer state={state} onBook={onBook} />
    </Modal>
  );
}

function NextFreeWindowAnswer({ state, onBook }: { state: ReturnType<typeof useNextFreeWindow>['state']; onBook: NextFreeWindowModalProps['onBook'] }) {
  if (state.status === 'error') return <Alert className="mt-5" type="error" title={state.message} showIcon />;
  if (state.status !== 'answered') return null;

  const { window, blockedBy } = state.answer;
  if (window) {
    return (
      <Alert
        className="mt-5"
        type="success"
        showIcon
        title={`Free ${formatWindow(window.startsAt, window.endsAt)}`}
        action={onBook && <Button onClick={() => onBook(window)}>Book this window</Button>}
      />
    );
  }
  return (
    <Alert
      className="mt-5"
      type="warning"
      showIcon
      title="No free window before then"
      description={
        blockedBy.length === 0 ? (
          'That date has already passed.'
        ) : (
          <ul className="m-0 pl-4">
            {blockedBy.map((entry) => (
              <li key={`${entry.kind}-${entry.id}`}>
                {entry.label}: {formatWindow(entry.startsAt, entry.endsAt)}
              </li>
            ))}
          </ul>
        )
      }
    />
  );
}
